import Box from '@mui/material/Box';
import React, { useEffect, useRef, useState } from 'react';

import { KeepMountedTabPanelProps } from './KeepMountedTabPanel.props';

/**
 * A tab panel that mounts lazily on first activation and then stays mounted,
 * hidden rather than unmounted when another tab is selected.
 *
 * This is deliberate, not an optimisation. Both pages using it put one form
 * behind several tabs: the Part Type sub-panels keep draft rows in local state,
 * and the structure detail page has a single Save that commits address, contact,
 * trading-hour and capacity edits together. Unmounting a panel on tab switch
 * would throw a half-filled row — or a whole tab's worth of unsaved edits — away.
 * Mounting lazily keeps the first paint as cheap as a single-column page: a
 * visitor who only reads the first tab never mounts the grids behind the others.
 */
export const KeepMountedTabPanel: React.FC<KeepMountedTabPanelProps> = ({
  tabKey,
  activeTab,
  idPrefix,
  children,
}) => {
  const active = activeTab === tabKey;
  const [mounted, setMounted] = useState(active);

  useEffect(() => {
    if (active) {
      setMounted(true);
    }
  }, [active]);

  // Hold the element a hidden panel last rendered. Re-rendering it with the same
  // element object lets React skip the whole subtree, so typing in one tab does
  // not re-render the grids and tables sitting hidden in the others — while
  // their state survives, which is the point of keeping them mounted.
  const rendered = useRef(children);
  if (active) {
    rendered.current = children;
  }

  if (!mounted) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      id={`${idPrefix}-panel-${tabKey}`}
      aria-labelledby={`${idPrefix}-tab-${tabKey}`}
      hidden={!active}
      // `hidden` alone loses to emotion's styles; display:none is what actually
      // takes the subtree out of the tab order and the accessibility tree.
      sx={{ display: active ? 'block' : 'none' }}
    >
      {rendered.current}
    </Box>
  );
};
